import { useQuery } from '@tanstack/react-query';
import { useEffect, type JSX } from 'react';
import { Controller } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { CustomFieldValuesForm } from '@/components/machines/CustomFieldValuesForm';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useZodForm } from '@/lib/forms/useZodForm';
import { getMachineTypeRequest, listMachineTypesRequest } from '@/services/machine-types';
import { listSitesRequest } from '@/services/sites';
import type { CustomField, MachineTypeDetail, MachineTypeListItem } from '@/types/machine-types';
import type {
  CreateMachineBody,
  CustomFieldValuesShape,
  MachineDetail,
  UpdateMachineBody,
} from '@/types/machines';
import type { PaginatedResult } from '@/types/roles';
import type { SiteListItem } from '@/types/sites';

/**
 * Plan 11-02 — MachineForm
 *
 * Shared between create and edit modes. Schema mirrors Plan 11-01:
 *  - name: 3..200 chars
 *  - code: 2..30 alnum/hyphens, regex `^[A-Za-z0-9-]{2,30}$`
 *  - machineTypeId: positive int (CREATE only — D-103 locks the type after creation)
 *  - siteId: optional positive int (CREATE only — null === unallocated; the
 *    backend opens an initial allocation row when set)
 *  - customFieldValues: Record<string, unknown> validated against the loaded
 *    MachineType.customFields schema (text/number/date/dropdown + required)
 *
 * The selected MachineType drives a chained `getMachineTypeRequest` query
 * that loads the persisted schema; whenever the schema resolves, the
 * `customFieldValues` slice is reset (`setValue('customFieldValues', {})`)
 * to prevent stale-value carry-over from a prior selection.
 *
 * D-103: in `mode='edit'` the MachineType `<Select>` is **disabled**; the
 * persisted MachineType is read from `initial.machineType` and used purely
 * to drive the schema-loading query.
 */

const MACHINE_CODE_REGEX = /^[A-Za-z0-9-]{2,30}$/;

const machineFormSchema = z.object({
  name: z.string().trim().min(3, 'At least 3 characters').max(200, 'At most 200 characters'),
  code: z
    .string()
    .trim()
    .regex(MACHINE_CODE_REGEX, 'Code must be 2-30 chars: letters, digits, hyphens'),
  machineTypeId: z.number().int().positive('Required'),
  siteId: z.number().int().positive().nullable(),
  customFieldValues: z.record(z.string(), z.unknown()),
});

type MachineFormShape = z.infer<typeof machineFormSchema>;

export interface MachineFormProps {
  readonly mode: 'create' | 'edit';
  readonly initial?: MachineDetail;
  readonly onSubmit: (body: CreateMachineBody | UpdateMachineBody) => void | Promise<void>;
  readonly submitting?: boolean;
  readonly errorMessage?: string | null;
}

const EMPTY_DEFAULTS: MachineFormShape = {
  name: '',
  code: '',
  machineTypeId: 0,
  siteId: null,
  customFieldValues: {},
};

function buildDefaults(
  mode: 'create' | 'edit',
  initial: MachineDetail | undefined,
): MachineFormShape {
  if (initial === undefined) return EMPTY_DEFAULTS;
  return {
    name: initial.name,
    code: initial.code,
    machineTypeId: initial.machineType.id,
    siteId: mode === 'edit' ? null : (initial.currentSite?.id ?? null),
    customFieldValues: { ...initial.customFieldValues },
  };
}

function validateAgainstSchema(
  schema: readonly CustomField[],
  values: Readonly<Record<string, unknown>>,
): { fieldKey: string; message: string } | null {
  for (const f of schema) {
    const v = values[f.key];
    if (f.required && (v === undefined || v === null || v === '')) {
      return { fieldKey: f.key, message: 'Required' };
    }
    if (v === undefined || v === null || v === '') continue;
    if (f.dataType === 'dropdown' && f.dropdownOptions !== null) {
      if (typeof v !== 'string' || !f.dropdownOptions.includes(v)) {
        return { fieldKey: f.key, message: 'Pick a valid option' };
      }
    } else if (f.dataType === 'number') {
      if (typeof v !== 'number' || !Number.isFinite(v)) {
        return { fieldKey: f.key, message: 'Must be a number' };
      }
    } else if (f.dataType === 'date') {
      if (typeof v !== 'string' || Number.isNaN(Date.parse(v))) {
        return { fieldKey: f.key, message: 'Must be a valid date' };
      }
    }
  }
  return null;
}

export function MachineForm({
  mode,
  initial,
  onSubmit,
  submitting = false,
  errorMessage,
}: MachineFormProps): JSX.Element {
  const defaults: MachineFormShape = buildDefaults(mode, initial);

  const form = useZodForm({
    schema: machineFormSchema,
    defaultValues: defaults,
  });

  // Re-sync defaults when initial changes (edit mode hydrates async).
  useEffect(() => {
    if (initial !== undefined) {
      form.reset(buildDefaults(mode, initial));
    }
    // form.reset is stable; intentionally only depend on initial identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  // Watched MachineType id drives the chained schema query.
  const watchedTypeId = form.watch('machineTypeId');

  const machineTypesQuery = useQuery<PaginatedResult<MachineTypeListItem>>({
    queryKey: ['machine-types', 'active', 'forMachineForm'],
    queryFn: () => listMachineTypesRequest({ status: 'active', page: 1, pageSize: 100 }),
    enabled: mode === 'create',
  });

  const machineTypeQuery = useQuery<MachineTypeDetail>({
    queryKey: ['machine-type', watchedTypeId],
    queryFn: () => getMachineTypeRequest(watchedTypeId),
    enabled: typeof watchedTypeId === 'number' && watchedTypeId > 0,
    staleTime: 30_000,
  });

  // When a MachineType loads in CREATE mode, reset customFieldValues so
  // stale prior-type values don't bleed across (T-11-21).
  useEffect(() => {
    if (mode === 'create' && machineTypeQuery.data !== undefined) {
      form.setValue('customFieldValues', {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, machineTypeQuery.data?.id]);

  // Active sites for the Site select (CREATE only — edit mode hides Site).
  const sitesQuery = useQuery<PaginatedResult<SiteListItem>>({
    queryKey: ['sites', 'active', 'forMachineForm'],
    queryFn: () => listSitesRequest({ status: 'active', page: 1, pageSize: 100 }),
    enabled: mode === 'create',
  });

  const schema: readonly CustomField[] = machineTypeQuery.data?.customFields ?? [];

  function handleSubmit(values: MachineFormShape): void {
    // Guard: if a machine type is selected but its schema hasn't loaded yet, block submit.
    // Without this guard, validateAgainstSchema receives an empty schema and all custom-field
    // values pass silently (validation gap fix).
    if (watchedTypeId > 0 && machineTypeQuery.data === undefined) {
      return;
    }

    // Cross-field validation against the MachineType's CustomField schema.
    const cfErr = validateAgainstSchema(schema, values.customFieldValues);
    if (cfErr !== null) {
      form.setError(`customFieldValues.${cfErr.fieldKey}`, {
        type: 'custom',
        message: cfErr.message,
      });
      return;
    }

    if (mode === 'create') {
      const body: CreateMachineBody = {
        name: values.name.trim(),
        code: values.code.trim(),
        machineTypeId: values.machineTypeId,
        siteId: values.siteId ?? null,
        customFieldValues: values.customFieldValues as CustomFieldValuesShape,
      };
      void onSubmit(body);
    } else {
      const body: UpdateMachineBody = {
        name: values.name.trim(),
        code: values.code.trim(),
        customFieldValues: values.customFieldValues as CustomFieldValuesShape,
      };
      void onSubmit(body);
    }
  }

  const machineTypeOptions = machineTypesQuery.data?.items ?? [];
  const siteOptions = sitesQuery.data?.items ?? [];

  return (
    <form
      onSubmit={(e) => {
        void form.handleSubmit(handleSubmit)(e);
      }}
      className="space-y-6"
      noValidate
      data-testid={`machine-form-${mode}`}
    >
      {errorMessage != null && errorMessage !== '' && (
        <div
          role="alert"
          data-testid="machine-form-error"
          className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
        >
          {errorMessage}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="machine-name">Name</Label>
          <Input
            id="machine-name"
            type="text"
            autoComplete="off"
            disabled={submitting}
            aria-invalid={form.formState.errors.name !== undefined}
            {...form.register('name')}
            data-testid="machine-form-name"
          />
          {form.formState.errors.name && (
            <p className="text-xs text-destructive" data-testid="machine-form-name-error">
              {form.formState.errors.name.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="machine-code">Code</Label>
          <Input
            id="machine-code"
            type="text"
            autoComplete="off"
            disabled={submitting}
            aria-invalid={form.formState.errors.code !== undefined}
            {...form.register('code')}
            data-testid="machine-form-code"
          />
          {form.formState.errors.code && (
            <p className="text-xs text-destructive" data-testid="machine-form-code-error">
              {form.formState.errors.code.message}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="machine-type">Machine type</Label>
        {mode === 'edit' ? (
          <div
            className="rounded-md border border-input bg-muted px-3 py-2 text-sm"
            data-testid="machine-form-machinetype-disabled"
          >
            {initial?.machineType.name ?? '—'}
            <span className="ml-2 text-xs text-muted-foreground">
              (locked — cannot change after creation)
            </span>
          </div>
        ) : (
          <Controller
            control={form.control}
            name="machineTypeId"
            render={({ field }) => (
              <Select
                value={
                  typeof field.value === 'number' && field.value > 0 ? String(field.value) : ''
                }
                onValueChange={(v) => {
                  const n = Number(v);
                  if (Number.isFinite(n) && n > 0) field.onChange(n);
                }}
                disabled={submitting || machineTypesQuery.isLoading}
              >
                <SelectTrigger id="machine-type" data-testid="machine-form-machinetype-trigger">
                  <SelectValue placeholder="Select a machine type…" />
                </SelectTrigger>
                <SelectContent>
                  {machineTypeOptions.map((t) => (
                    <SelectItem
                      key={t.id}
                      value={String(t.id)}
                      data-testid={`machine-form-machinetype-option-${String(t.id)}`}
                    >
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        )}
        {form.formState.errors.machineTypeId && (
          <p className="text-xs text-destructive" data-testid="machine-form-machinetype-error">
            {form.formState.errors.machineTypeId.message}
          </p>
        )}
      </div>

      {mode === 'create' && (
        <div className="space-y-2">
          <Label htmlFor="machine-site">Initial site (optional)</Label>
          <Controller
            control={form.control}
            name="siteId"
            render={({ field }) => (
              <Select
                value={
                  typeof field.value === 'number' && field.value > 0 ? String(field.value) : 'none'
                }
                onValueChange={(v) => {
                  if (v === 'none') {
                    field.onChange(null);
                  } else {
                    const n = Number(v);
                    if (Number.isFinite(n) && n > 0) field.onChange(n);
                  }
                }}
                disabled={submitting || sitesQuery.isLoading}
              >
                <SelectTrigger id="machine-site" data-testid="machine-form-site-trigger">
                  <SelectValue placeholder="No initial site" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" data-testid="machine-form-site-option-none">
                    — Unallocated —
                  </SelectItem>
                  {siteOptions.map((s) => (
                    <SelectItem
                      key={s.id}
                      value={String(s.id)}
                      data-testid={`machine-form-site-option-${String(s.id)}`}
                    >
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          <p className="text-xs text-muted-foreground">
            Setting a site here opens the initial allocation row in the same transaction.
          </p>
        </div>
      )}

      <div className="space-y-2">
        <Label className="text-sm font-medium">Custom fields</Label>
        {watchedTypeId > 0 ? (
          machineTypeQuery.isLoading ? (
            <p className="text-sm text-muted-foreground" data-testid="machine-form-cfv-loading">
              Loading custom field schema…
            </p>
          ) : (
            <CustomFieldValuesForm
              schema={schema}
              control={form.control}
              setValue={form.setValue}
              errors={form.formState.errors}
              disabled={submitting}
            />
          )
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="machine-form-cfv-pick-type">
            Select a machine type to load its custom-field schema.
          </p>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button
          type="submit"
          disabled={submitting || (watchedTypeId > 0 && machineTypeQuery.data === undefined)}
          data-testid="machine-form-submit"
        >
          {submitting
            ? mode === 'create'
              ? 'Creating…'
              : 'Saving…'
            : mode === 'create'
              ? 'Create machine'
              : 'Save changes'}
        </Button>
        <Button asChild variant="outline" type="button">
          <Link to="/machines" data-testid="machine-form-cancel">
            Cancel
          </Link>
        </Button>
      </div>
    </form>
  );
}
